import { Editor, BaseTemplate } from 'cio/services/editor';
import config from 'cio/config/environment';

type BeeTemplateTag =
  | 'announcement'
  | 'call_to_action'
  | 'template'
  | 'menu'
  | 'feedback'
  | 'nps'
  | 'cart'
  | 'sale'
  | 'survey'
  | 'transactional'
  | 'launch'
  | 'date'
  | 'download'
  | 'ebook'
  | 'promotion'
  | 'note'
  | 'anniversary'
  | 'birthday'
  | 'newsletter'
  | 'letter'
  | 'ecommerce'
  | 'commerce'
  | 'seasonal'
  | 'feature'
  | 'welcome'
  | 'app'
  | 'onboarding'
  | 'conference'
  | 'receipt'
  | 'invoice'
  | 'promotion'
  | 'marketing'
  | 'event';

const attrs: {
  id: string;
  name: string;
  tags: BeeTemplateTag[];
}[] = [
  { id: 'base-newsletter', name: 'Newsletter Template', tags: ['template', 'newsletter'] },
  { id: 'base-menu', name: 'Menu Template', tags: ['template', 'menu'] },
  { id: 'base-cta', name: 'Call to Action Template', tags: ['template', 'call_to_action'] },
  { id: 'base-announcement', name: 'Announcement Template', tags: ['template', 'announcement'] },
  {
    id: 'nps',
    name: 'Net Promoter Score',
    tags: ['template', 'feedback', 'nps', 'survey', 'transactional'],
  },
  {
    id: 'qr-code',
    name: 'Offer Download',
    tags: ['template', 'launch', 'download', 'ebook', 'promotion', 'transactional'],
  },
  {
    id: 'happy-birthday',
    name: 'Birthday & Anniversary',
    tags: ['template', 'note', 'anniversary', 'birthday', 'promotion'],
  },
  {
    id: 'minimalistic',
    name: 'Letter',
    tags: ['template', 'newsletter', 'note', 'letter'],
  },
  {
    id: 'browsing-products',
    name: 'Abandoned Cart',
    tags: ['template', 'cart', 'sale', 'ecommerce'],
  },
  {
    id: 'black-friday-cosmetics',
    name: 'Product Launch',
    tags: ['template', 'launch', 'sale', 'commerce', 'promotion', 'seasonal'],
  },
  {
    id: 'black-friday-version-1',
    name: 'Seasonal Sale',
    tags: ['template', 'sale', 'ecommerce', 'promotion', 'seasonal'],
  },
  { id: 'cryptonboarding', name: 'Get Started', tags: ['template', 'app', 'feature', 'welcome', 'onboarding'] },
  { id: 'features-discovery', name: 'App Walkthrough', tags: ['template', 'app', 'feature', 'welcome', 'onboarding'] },
  { id: 'plugin', name: 'Product Feature', tags: ['template', 'app', 'feature', 'launch', 'marketing'] },
  { id: 'the-conference', name: 'Upcoming Event', tags: ['template', 'conference', 'date', 'event'] },
  {
    id: 'product-invoice',
    name: 'Detailed Invoice',
    tags: ['template', 'ecommerce', 'invoice', 'receipt', 'transactional'],
  },
  {
    id: 'light-invoice',
    name: 'Minimal Invoice',
    tags: ['template', 'ecommerce', 'invoice', 'receipt', 'transactional'],
  },
  { id: 'onechance-nonprofit', name: 'Newsletter', tags: ['template', 'newsletter', 'marketing'] },
];

export class BeeTemplate implements BaseTemplate {
  id: string;
  name: string;
  tags: BeeTemplateTag[];

  body = '';
  bodyJson = '';
  layout = '';
  editor = Editor.DRAG_AND_DROP;

  constructor(id: string, name: string, tags: BeeTemplateTag[]) {
    this.id = id;
    this.name = name;
    this.tags = tags;
  }

  get previewImageUrl() {
    // Load sharper images on hidpi devices only.
    return `${this.baseUrl}/${this.id}${window.devicePixelRatio === 2 ? '_large' : ''}.jpg`;
  }

  get htmlUrl() {
    return `${this.baseUrl}/${this.id}.html`;
  }

  get jsonUrl() {
    return `${this.baseUrl}/${this.id}.json`;
  }

  async fetchContent() {
    let res = await fetch(this.jsonUrl);
    let json = await res.json();

    this.bodyJson = JSON.stringify(json);
    return this;
  }

  private get baseUrl() {
    if (config.isProduction) {
      return `https://customerio-bee.s3.amazonaws.com/bee-templates`;
    } else {
      return `https://customerio-bee-standalone.s3.amazonaws.com/bee-templates`;
    }
  }
}

export default attrs.map((args) => new BeeTemplate(args.id, args.name, args.tags));
